/**
 * AppLayout — autenticazione Phase 6 (IMPL-P6-006).
 *
 * Minimal shell for authenticated routes. Renders:
 *  - Top navbar:    brand mark (FileCheck2) + portal name + user email +
 *                   role badge + LogoutMenu
 *  - UnverifiedEmailBanner (sticky, no-op when verified)
 *  - <main>:        either explicit `children` (used by dashboard pages
 *                   that wrap themselves) or React Router `<Outlet />`
 *                   so the same layout can also be mounted as a layout
 *                   route in the future.
 */
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Archive,
  FileCheck2,
  FolderClock,
  HelpCircle,
  PackagePlus,
  Receipt,
  RefreshCw,
  Tags,
  Wallet,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthSession } from '@/features/auth/hooks/useAuthSession';
import { LogoutMenu } from '@/features/auth/components/LogoutMenu';
import { UnverifiedEmailBanner } from '@/features/auth/components/UnverifiedEmailBanner';
import { BillingProfileProvider } from '@/features/profilo/hooks/useBillingProfile';
import { ProfileCompletenessBadge } from '@/features/profilo/components/ProfileCompletenessBadge';
import { SupportTicketModal } from '@/features/support';

const ROLE_LABEL: Record<string, string> = {
  CLIENT: 'Cliente',
  OPERATOR: 'Operatore',
  ADMIN: 'Admin',
};

interface AppLayoutProps {
  children?: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user } = useAuthSession();
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <BillingProfileProvider>
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <header className="border-b bg-background">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
            <div className="flex items-center gap-2">
              <FileCheck2 className="h-6 w-6 text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold tracking-tight">
                Portale Registrazione Contratti
              </span>
            </div>

            {user ? (
              <div className="flex items-center gap-3">
                {user.role === 'ADMIN' ? (
                  /* F-012 + F-009.4: admin nav — gestione fido + listino prezzi */
                  <>
                    <NavLink
                      to="/admin/clienti"
                      className={({ isActive }) =>
                        [
                          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
                          isActive
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        ].join(' ')
                      }
                    >
                      <Wallet className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Gestione Fido</span>
                    </NavLink>
                    <NavLink
                      to="/admin/listino"
                      className={({ isActive }) =>
                        [
                          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
                          isActive
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        ].join(' ')
                      }
                    >
                      <Tags className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Listino</span>
                    </NavLink>
                    <NavLink
                      to="/admin/servizi"
                      className={({ isActive }) =>
                        [
                          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
                          isActive
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        ].join(' ')
                      }
                    >
                      <PackagePlus className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Servizi</span>
                    </NavLink>
                    <NavLink
                      to="/admin/riconciliazione"
                      className={({ isActive }) =>
                        [
                          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
                          isActive
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        ].join(' ')
                      }
                    >
                      <RefreshCw className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Riconciliazione</span>
                    </NavLink>
                    <NavLink
                      to="/admin/fatture"
                      className={({ isActive }) =>
                        [
                          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
                          isActive
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        ].join(' ')
                      }
                    >
                      <Receipt className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Fatture</span>
                    </NavLink>
                  </>
                ) : (
                  <>
                    {/* F-005: Le mie pratiche (lista pratiche attive, CLIENT) */}
                    <NavLink
                      to="/pratiche"
                      className={({ isActive }) =>
                        [
                          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
                          isActive
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        ].join(' ')
                      }
                    >
                      <FolderClock className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Le mie pratiche</span>
                    </NavLink>
                    {/* F-006: Archivio nav link */}
                    <NavLink
                      to="/app/archivio"
                      className={({ isActive }) =>
                        [
                          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
                          isActive
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        ].join(' ')
                      }
                    >
                      <Archive className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Archivio</span>
                    </NavLink>
                    {/* F-009.3: Wallet nav link — solo utenti business (AGENTE/
                        COMMERCIALISTA); i privati usano il pay-per-practice. */}
                    {user.tipologia === 'AGENTE' || user.tipologia === 'COMMERCIALISTA' ? (
                      <NavLink
                        to="/app/wallet"
                        className={({ isActive }) =>
                          [
                            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
                            isActive
                              ? 'bg-muted text-foreground'
                              : 'text-muted-foreground hover:text-foreground',
                          ].join(' ')
                        }
                      >
                        <Wallet className="h-4 w-4" aria-hidden="true" />
                        <span className="hidden sm:inline">Wallet</span>
                      </NavLink>
                    ) : null}
                    {/* F-002: completeness badge — visible su >= sm. */}
                    <ProfileCompletenessBadge />
                  </>
                )}
                {/* F-013: link "Contatta supporto" persistente, accessibile da ogni screen. */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-muted-foreground hover:text-foreground"
                  onClick={() => setSupportOpen(true)}
                >
                  <HelpCircle className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Contatta supporto</span>
                </Button>
                <span className="hidden text-sm text-muted-foreground sm:inline">
                  {user.email}
                </span>
                <Badge variant="outline">{ROLE_LABEL[user.role] ?? user.role}</Badge>
                <LogoutMenu />
              </div>
            ) : null}
          </div>
        </header>

        {/* Montato solo on-demand: evita di richiedere un QueryClientProvider
            (usato da `useCreateSupportTicket`) in ogni albero che monta
            `AppLayout`, incluse le pagine/test che non toccano mai il supporto. */}
        {supportOpen ? (
          <SupportTicketModal open={supportOpen} onOpenChange={setSupportOpen} />
        ) : null}

        <UnverifiedEmailBanner />

        <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
          {children ?? <Outlet />}
        </main>
      </div>
    </BillingProfileProvider>
  );
}
